import React, { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../lib/auth';
import { StageBadge } from '../components/SeverityBadge';
import { ArrowRight, ShieldCheck, FileCheck2, Scan, BookCheck, Sparkles } from 'lucide-react';
import api from '../lib/api';
import { AuditDemoMotion } from '../components/AuditDemoMotion';

export default function Home() {
  const { user } = useAuth();
  const [news, setNews] = useState([]);
  const [lastCrawl, setLastCrawl] = useState(null);

  useEffect(() => {
    api.get('/news').then(({ data }) => {
      setNews(data.items || []);
      setLastCrawl(data.last_crawled_at);
    }).catch(() => {});
  }, []);

  // Super-admin lives in the admin console; never sees the marketing/audit surface.
  if (user?.role === 'super_admin') return <Navigate to="/admin" replace />;

  const stages = ['draft_gazette', 'public_consultation', 'final_published', 'enforcement'];
  const byStage = (s) => news.filter((n) => n.stage === s);

  return (
    <Layout>
      {/* HERO */}
      <section className="border-b border-border" data-testid="hero-section">
        <div className="mx-auto max-w-[1400px] grid grid-cols-1 lg:grid-cols-12 px-6 md:px-10">
          <div className="lg:col-span-7 py-20 md:py-28 pr-0 lg:pr-12 animate-fade-up">
            <div className="inline-flex items-center gap-2 mb-6 font-mono text-[10px] uppercase tracking-widest text-accent border-gradient-pt px-3 py-1.5" data-testid="hero-free-hook">
              <Sparkles size={11} strokeWidth={1.8} /> First check free — no card required
            </div>
            <div className="label-mono mb-6" data-testid="hero-tagline">PackCheck / Compliance Audit Engine — v0.1</div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-medium tracking-tight leading-[1.05]">
              Your label.<br />
              <span className="text-gradient-pt">Audited before it prints.</span>
            </h1>
            <p className="mt-8 max-w-xl text-base text-muted-foreground leading-relaxed">
              An FSSAI &amp; Legal Metrology artwork compliance auditor for food packaging designers and FBOs.
              Upload a print-ready PDF — PackCheck identifies the PDP and BOP, reads the slug, analyses font sizes and
              placement, evaluates nomenclature, and runs <span className="font-mono text-foreground">60+ compliance checks</span> with regulation citations.
            </p>
            <div className="mt-10 flex flex-wrap gap-3 items-center">
              <Link to="/audit/new" data-testid="cta-check-pack" className="inline-flex items-center gap-2 h-12 px-6 bg-foreground text-background hover:opacity-90 transition-opacity">
                Check a pack <ArrowRight size={16} strokeWidth={1.5} />
              </Link>
              <Link to="/pricing" data-testid="cta-pricing" className="inline-flex items-center gap-2 h-12 px-6 border border-border hover:bg-surface-hover transition-colors">
                View pricing
              </Link>
            </div>

            {/* Free-audit hook — full-width pill BELOW the CTAs */}
            <Link
              to="/audit/new"
              data-testid="cta-free-badge"
              className="mt-6 inline-flex items-center gap-4 pl-2 pr-6 py-2 rounded-full border border-teal-400 bg-cyan-100 hover:bg-cyan-200/80 dark:bg-teal-950/30 dark:hover:bg-teal-950/50 transition-colors max-w-xl"
            >
              <span className="w-10 h-10 grid place-items-center rounded-full bg-teal-400 text-black font-mono text-base font-semibold shrink-0">1</span>
              <span className="text-base md:text-lg leading-snug">
                <span className="text-teal-500 dark:text-teal-400 font-medium">First audit free</span>
                <span className="text-foreground"> — no card needed. Sign in to claim.</span>
              </span>
            </Link>

            <dl className="mt-16 grid grid-cols-3 gap-6 max-w-xl">
              {[
                ['32', 'Pack formats'],
                ['60+', 'Compliance checks'],
                ['Weekly', 'FSSAI feed'],
              ].map(([k, v]) => (
                <div key={v} className="border-l border-border pl-4">
                  <div className="text-3xl font-mono">{k}</div>
                  <div className="label-mono mt-1">{v}</div>
                </div>
              ))}
            </dl>
          </div>

          <div className="lg:col-span-5 border-l border-border min-h-[420px] grid-bg relative">
            <AuditDemoMotion />
          </div>
        </div>
      </section>

      {/* FEATURE GRID */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 md:px-10 py-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-border">
          {[
            { i: Scan, t: 'Visual + Text intelligence', d: 'PDP/BOP panel identification, font-size estimation, slug reading, keyline structural checks.' },
            { i: BookCheck, t: 'Cited regulation findings', d: 'Every flag carries the exact FSSAI / LMPC regulation reference and a suggested fix.' },
            { i: FileCheck2, t: 'Pack-format-aware', d: '32 KLD layouts — pillow pouches, doypacks, cartons, cans, bottles, sleeves. Format-specific rules.' },
            { i: ShieldCheck, t: 'Always current', d: 'Live weekly feed of FSSAI gazette notifications, advisories, and Legal Metrology updates.' },
          ].map(({ i: Icon, t, d }) => (
            <div key={t} className="bg-background p-8">
              <div className="w-10 h-10 grid place-items-center gradient-pt-soft">
                <Icon size={20} strokeWidth={1.5} className="text-foreground" />
              </div>
              <div className="mt-6 font-medium">{t}</div>
              <div className="mt-2 text-sm text-muted-foreground leading-relaxed">{d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* NEWS PIPELINE */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 md:px-10 py-20">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="label-mono mb-2">Regulatory pipeline</div>
              <h2 className="text-3xl md:text-4xl font-medium tracking-tight">FSSAI &amp; Legal Metrology updates</h2>
            </div>
            <div className="text-right">
              <div className="label-mono">Last refresh</div>
              <div className="font-mono text-sm">{lastCrawl ? new Date(lastCrawl).toLocaleString() : 'Seed data'}</div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-border" data-testid="news-pipeline">
            {stages.map((s) => (
              <div key={s} className="bg-background p-6">
                <div className="flex items-center justify-between mb-4">
                  <StageBadge stage={s} />
                  <span className="font-mono text-[11px] text-muted-foreground">{byStage(s).length}</span>
                </div>
                <div className="space-y-4">
                  {byStage(s).slice(0, 3).map((n) => (
                    <a key={n.news_id} href={n.source_url} target="_blank" rel="noreferrer" className="block group">
                      <div className="text-sm font-medium leading-snug group-hover:text-accent transition-colors">{n.title}</div>
                      <div className="mt-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{n.source === 'fssai' ? 'FSSAI' : 'Legal Metrology'}</div>
                    </a>
                  ))}
                  {byStage(s).length === 0 && (
                    <div className="text-sm text-muted-foreground">No items.</div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8">
            <Link to="/updates" className="text-sm inline-flex items-center gap-2 text-gradient-pt font-medium">All updates <ArrowRight size={14} strokeWidth={1.5} className="text-foreground" /></Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
